// ─────────────────────────────────────────────────────────────────
// FindRE — Three-way sync merge
//
// Problem this solves:
//   Two devices (Ibrahim + Hamed) each hold a snapshot of the data
//   taken when their tab loaded. Any save used to upload that whole
//   snapshot, silently overwriting whatever the other person had
//   changed in the meantime.
//
// Fix:
//   Before writing a key to Supabase we compare three versions:
//     base   — what the cloud held last time WE synced
//     local  — what this browser has now
//     remote — what the cloud holds right now
//   and merge them, so additions and deletions from BOTH sides survive.
//
// Rules (per record / per field):
//   in local, not in base   → we added it        → keep
//   in base, not in local   → we deleted it      → drop
//   in remote, not in base  → they added it      → keep
//   in base, not in remote  → they deleted it    → drop
//   in both, different      → whoever actually changed it wins;
//                             if BOTH changed it, prefer local (we are writing)
//
// Anything the merger can't identify falls back to "local wins",
// which is the old behaviour — so this can never be worse.
// ─────────────────────────────────────────────────────────────────

/** Business identity of a record. Invoices use invoiceNo, everything else uses id. */
export function identityOf(rec) {
  if (!rec || typeof rec !== "object") return null;
  if (rec.id !== undefined && rec.id !== null) return `id:${rec.id}`;
  if (rec.invoiceNo !== undefined && rec.invoiceNo !== null) return `invoiceNo:${rec.invoiceNo}`;
  return null;
}

export function isPlainObject(v) {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

/** True when every element carries a usable identity. */
export function isIdentifiableArray(arr) {
  return Array.isArray(arr) && arr.length > 0 && arr.every(el => identityOf(el) !== null);
}

export function deepEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (typeof a !== "object") return false;
  const ka = Object.keys(a), kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every(k => Object.prototype.hasOwnProperty.call(b, k) && deepEqual(a[k], b[k]));
}

function indexById(arr) {
  const m = new Map();
  if (!Array.isArray(arr)) return m;
  arr.forEach(el => {
    const key = identityOf(el);
    if (key !== null) m.set(key, el);
  });
  return m;
}

function mergeArrays(base, local, remote) {
  const B = indexById(base), L = indexById(local), R = indexById(remote);
  const out = [];
  const taken = new Set();

  // Walk local first so the user's own ordering is preserved.
  for (const el of local) {
    const key = identityOf(el);
    if (key === null) { out.push(el); continue; }
    taken.add(key);
    const inRemote = R.has(key), inBase = B.has(key);
    if (inRemote) {
      out.push(threeWayMerge(B.get(key), el, R.get(key)));
    } else if (inBase) {
      // present in base, gone from remote → the other device deleted it
      continue;
    } else {
      out.push(el); // we added it
    }
  }

  // Records only the other device has.
  for (const el of remote) {
    const key = identityOf(el);
    if (key === null || taken.has(key)) continue;
    if (B.has(key)) continue;   // we deleted it
    out.push(el);               // they added it
  }

  return out;
}

function mergeObjects(base, local, remote) {
  const out = {};
  const keys = new Set([...Object.keys(local), ...Object.keys(remote)]);

  for (const k of keys) {
    const inL = Object.prototype.hasOwnProperty.call(local, k);
    const inR = Object.prototype.hasOwnProperty.call(remote, k);
    const inB = Object.prototype.hasOwnProperty.call(base, k);

    if (inL && inR) {
      out[k] = threeWayMerge(base[k], local[k], remote[k]);
    } else if (inL && !inR) {
      if (!inB) out[k] = local[k];        // we added it
      // else: they deleted it → drop
    } else if (!inL && inR) {
      if (!inB) out[k] = remote[k];       // they added it
      // else: we deleted it → drop
    }
  }
  return out;
}

/**
 * Merge one data set.
 * @param {*} base   value the cloud held at our last sync (may be undefined)
 * @param {*} local  this browser's current value
 * @param {*} remote the cloud's current value
 */
export function threeWayMerge(base, local, remote) {
  if (remote === undefined || remote === null) return local;
  if (local === undefined || local === null) return remote;
  if (deepEqual(local, remote)) return local;

  // We never touched this since our last sync, but they did → take theirs.
  // Without this, a stale tab re-uploads old values and wipes the other device.
  if (base !== undefined && deepEqual(local, base)) return remote;

  if (Array.isArray(local) && Array.isArray(remote)) {
    if (isIdentifiableArray(local) && isIdentifiableArray(remote)) {
      return mergeArrays(Array.isArray(base) ? base : [], local, remote);
    }
    return local; // can't identify records — writer wins (old behaviour)
  }

  if (isPlainObject(local) && isPlainObject(remote)) {
    return mergeObjects(isPlainObject(base) ? base : {}, local, remote);
  }

  return local; // scalars / mismatched types — writer wins
}
